import { _ } from '@splunk/ui-utils/i18n';
import { createRESTURL } from '@splunk/splunk-utils/url';
import { getDefaultFetchInit } from '@splunk/splunk-utils/fetch';
import type { RequestParams, SPL2ViewMetadata } from '@splunk/dashboard-types';

const SPL2_MODULES_ENDPOINT = 'orchestrator/v1/spl2/modules';

const APPS_REGEX = /^(~\.|(\w+\.)+)apps(\.[\w-]+){1,2}$/;
const PRIVATE_REGEX = /^(~\.|(\w+\.)+)users\.[\w-]+\.apps(\.[\w-]+){1,2}$/;

/**
 * Generates an SPL2 query from request parameters that can reference an exported view from a module
 *
 * @returns {string}
 * @private
 * @param view
 * @param namespace
 */
export const generateSPL2ViewQuery = (
    view: string,
    namespace?: string
): string => {
    const trimmedView = view.trim();
    const trimmedNamespace = namespace?.trim();
    if (!trimmedNamespace) {
        // if no namespace, fallback to default namespace
        return `| @spl2 | from ${trimmedView}`;
    }
    return `| @spl2 | from ${trimmedNamespace}.${trimmedView}`;
};

export const stripPostProcessRequestParams = (
    requestParams: RequestParams
): RequestParams => {
    const requestParamsWithoutPostprocess = { ...requestParams };
    delete requestParamsWithoutPostprocess.search;
    return requestParamsWithoutPostprocess;
};

/**
 * Gets the sharing from the namespace.
 *
 * Sharing is Private if namespace matches the following formats:
 * ~.users.<user_name>.apps.<app_name>
 * ~.users.<user_name>.apps.<app_name>.<module_name>
 * <some_path>.users.<user_name>.apps.<app_name>
 * <some_path>.users.<user_name>.apps.<app_name>.<module_name>
 *
 * Sharing is App if namespace matches the following formats:
 * ~.apps.<app_name>
 * ~.apps.<app_name>.<module_name>
 * <some_path>.apps.<app_name>
 * <some_path>.apps.<app_name>.<module_name>
 *
 * Sharing is Custom otherwise
 *
 * @param namespace
 */
export const getSharingFromNamespace = (namespace: string) => {
    if (PRIVATE_REGEX.test(namespace)) {
        return _('Private');
    }
    if (APPS_REGEX.test(namespace)) {
        return _('App');
    }
    return _('Custom');
};

/**
 * Gets the app name from the namespace. Returns null if namespace is not in one of the expected formats.
 *
 * Valid shorcut formats:
 * ~.apps.<app_name>
 * ~.apps.<app_name>.<module_name>
 * ~.users.<user_name>.apps.<app_name>
 * ~.users.<user_name>.apps.<app_name>.<module_name>
 *
 * Valid absolute formats:
 * <some_path>.apps.<app_name>
 * <some_path>.apps.<app_name>.<module_name>
 * <some_path>.users.<user_name>.apps.<app_name>
 * <some_path>.users.<user_name>.apps.<app_name>.<module_name>
 *
 * @param namespace
 */
export const getAppFromNamespace = (namespace: string) => {
    if (APPS_REGEX.test(namespace) || PRIVATE_REGEX.test(namespace)) {
        const namespaceArray = namespace.split('.apps.');
        return namespaceArray[namespaceArray.length - 1].split('.')[0];
    }
    return null;
};

export const getSPL2ModuleMetadata = async ({
    signal,
    namespace,
}: {
    signal?: AbortSignal;
    namespace: string;
}): Promise<SPL2ViewMetadata> => {
    let response = null;

    const url = createRESTURL(
        `${SPL2_MODULES_ENDPOINT}/${namespace}?output_mode=json`
    );
    try {
        const res = await fetch(url, {
            ...getDefaultFetchInit(),
            signal,
        });

        if (!res.ok) {
            throw new Error(res.statusText);
        }

        response = await res.json();
    } catch {
        // ignored
    }

    return {
        namespace: response?.namespace,
        name: response?.name,
        createdBy: response?.createdBy,
        updatedAt: response?.updatedAt,
        updatedBy: response?.updatedBy,
        displayName: response?.displayName,
        earliestTime: response?.earliestTime,
        latestTime: response?.latestTime,
        isScheduled: false,
    };
};
